import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/Button";
import { useAuth } from "../../hooks/useAuth";
import { dismissGetStarted } from "../../services/onboarding";
import type { GetStartedTask } from "../../types/onboarding";
import { canReachOrganizationSettings, getStartedRouteFor } from "./getStartedRoutes";
import { useGetStartedChecklist } from "./useGetStartedChecklist";
import "./GetStartedPanel.css";

/**
 * The Get Started checklist a new program sees above its players table.
 *
 * **Two rules decide whether this renders at all**, and both come from the server:
 *
 * 1. Show it when the user has not dismissed it **and** at least one item is undone. Nothing
 *    login-derived: `LastLoginAt` is rewritten by the auth middleware on every authenticated
 *    request, so it is already non-null before this page renders and a gate on it would show the
 *    panel to nobody. The API deliberately exposes no such signal.
 * 2. The item list is whatever the server returned. Its length varies by role and page permission,
 *    so a missing key means "not this caller's task", not "incomplete" — there is no fixed set of
 *    five, nothing is indexed positionally, and an absent key renders nothing at all.
 *
 * Completion and dismissal are both per user: an item ticks off when this user has done it, not
 * when a colleague has, so a coach joining an established program still gets the full list.
 */
export const GetStartedPanel = () => {
  const { user } = useAuth();
  // Deliberately no `includeItems`. This mounts on /players, the landing page, for every user on
  // every visit, and a dismissed checklist renders nothing here — so the server is asked for the
  // dismissal alone and skips the completion queries that this component would only throw away.
  // A user who has not dismissed it still gets the items: the server short-circuits on the
  // dismissal, not on the flag. GetStartedVisibilityCard is the caller that opts in.
  const { checklist, setChecklist, activeRef } = useGetStartedChecklist();
  const [dismissing, setDismissing] = useState(false);
  const [dismissError, setDismissError] = useState(false);
  const [justDismissed, setJustDismissed] = useState(false);
  const noticeRef = useRef<HTMLParagraphElement>(null);
  const headingId = useId();

  // Dismissing unmounts the button that holds focus, which would drop a keyboard user silently at
  // the top of the document. Focus moves to the confirmation that replaces it instead, so the
  // change is both announced and reachable from where they were.
  useEffect(() => {
    if (justDismissed) noticeRef.current?.focus();
  }, [justDismissed]);

  const handleDismiss = async () => {
    // aria-disabled leaves the handler attached, so this guard is what stops the second POST.
    if (dismissing) return;
    setDismissing(true);
    setDismissError(false);
    try {
      // Render from the server's answer rather than hiding optimistically. If the write did not
      // land, the panel is still here on the next page load, and silently pretending otherwise is
      // how a coach learns the button does not work.
      const updated = await dismissGetStarted();
      setChecklist(updated);
      if (!activeRef.current) return;
      // Confirm only what the server actually recorded. A 200 that comes back still undismissed
      // would otherwise say "Checklist hidden" to someone who finds it waiting again on reload.
      if (updated?.dismissedAt) setJustDismissed(true);
      else setDismissError(true);
    } catch {
      if (activeRef.current) setDismissError(true);
    } finally {
      if (activeRef.current) setDismissing(false);
    }
  };

  if (justDismissed) {
    // No `role="status"` on this notice: it is inserted with its text already present, and a live
    // region added that way is unreliably announced (AccountPausedScreen and
    // TrustedContactsSection document the same rule) while double-reading in the combinations that
    // do fire on insertion. The focus move in the effect above owns the announcement instead.
    return (
      <p
        className="get-started-panel__notice"
        tabIndex={-1}
        ref={noticeRef}
        data-testid="get-started-dismissed-notice"
      >
        {/*
          The pointer back is shown only to someone who can actually follow it. /settings is
          admin-only, so telling a coach to go there would be advice they cannot take.
        */}
        {canReachOrganizationSettings(user?.profile)
          ? "Checklist hidden. You can bring it back from organization settings."
          : "Checklist hidden."}
      </p>
    );
  }

  // No checklist at all: either an older API (the service reads a 404 as "no such feature") or a
  // read that failed. Both render nothing.
  if (!checklist) return null;

  // Truthy rather than `!== null` so an API that omits the field entirely reads as "not dismissed".
  // Hiding on an absent field would look identical to every user having dismissed it.
  if (checklist.dismissedAt) return null;

  // `?? []` is right here, where the fallback hides the panel: an API that sent no items has given
  // us nothing to show. The same expression is wrong in GetStartedVisibilityCard, where an absent
  // list would instead claim the program had finished every step and take away the way back.
  const items = checklist.items ?? [];
  if (!items.some((item) => !item.done)) return null;

  return (
    <section
      className="get-started-panel"
      aria-labelledby={headingId}
      data-testid="get-started-panel"
    >
      <div className="get-started-panel__header">
        <div>
          <h2 className="get-started-panel__title" id={headingId}>
            Get started
          </h2>
          <p className="get-started-panel__intro">
            A few things that make the rest of RosterPilot useful. Each one ticks off once you
            have done it yourself.
          </p>
        </div>
        {/*
          `loading` is not used here: it swaps the label for a hardcoded "Loading...", and "Hiding…"
          is the accurate word.

          `aria-disabled` rather than `disabled`, because this is the button the coach just pressed.
          A disabled button leaves the tab order the instant it is activated, so setting `dismissing`
          blurs it and drops focus to <body>. The success path moves focus to the notice that
          replaces this panel; the failure path leaves the panel up and used to strand a keyboard
          user at the top of the document. The guard in the handler stops the second POST.
        */}
        <Button
          variant="secondary"
          onClick={handleDismiss}
          aria-disabled={dismissing}
          aria-busy={dismissing}
          data-testid="get-started-panel-dismiss"
        >
          {dismissing ? "Hiding…" : "Hide checklist"}
        </Button>
      </div>

      <GetStartedItemList items={items} />

      {dismissError && (
        <p className="get-started-panel__error" role="alert">
          We could not hide the checklist just now. Try again in a moment.
        </p>
      )}
    </section>
  );
};

interface GetStartedItemListProps {
  /** What the server sent, in its order. Never padded or indexed: the set varies by caller. */
  items: GetStartedTask[];
  /**
   * Called when a coach follows a row's link. The sidebar dialog closes itself here, since the page
   * the link opens would otherwise sit behind it.
   */
  onFollow?: () => void;
}

/**
 * The checklist rows, shared by the panel above the players table and the sidebar's Get Started
 * dialog, so the two cannot drift on which rows link where.
 */
export const GetStartedItemList = ({ items, onFollow }: GetStartedItemListProps) => {
  const { user } = useAuth();
  return (
    <ul className="get-started-panel__items">
      {items.map((item) => (
        <GetStartedRow
          key={item.key}
          item={item}
          route={getStartedRouteFor(item.key, user?.profile)}
          onFollow={onFollow}
        />
      ))}
    </ul>
  );
};

interface GetStartedRowProps {
  item: GetStartedTask;
  /** Where this task lives in this client, or null when the key is one we do not recognise. */
  route: string | null;
  onFollow?: () => void;
}

/**
 * One checklist row.
 *
 * Done rows stay on the list as plain text: the point of showing them is the progress, and linking
 * a finished task invites a coach to redo it.
 *
 * An unrecognised key renders as plain text too. The server can add a task before this client knows
 * where it lives, and an instruction with no link beats a link to nowhere.
 */
const GetStartedRow = ({ item, route, onFollow }: GetStartedRowProps) => (
  <li
    className={`get-started-panel__item${item.done ? " get-started-panel__item--done" : ""}`}
    data-testid={`get-started-item-${item.key}`}
  >
    <span
      className={`get-started-panel__marker${
        item.done ? "" : " get-started-panel__marker--todo"
      }`}
      aria-hidden="true"
    >
      {item.done ? "✓" : "○"}
    </span>
    <div>
      {item.done || route === null ? (
        <span className="get-started-panel__label">{item.label}</span>
      ) : (
        <Link className="get-started-panel__link" to={route} onClick={onFollow}>
          {item.label}
        </Link>
      )}
      {/* Spoken, not coloured or struck through, so the state does not depend on seeing the row. */}
      {item.done && <span className="get-started-panel__sr-only"> (done)</span>}
      <p className="get-started-panel__description">{item.description}</p>
    </div>
  </li>
);
